/**
 * Sample-ratio mismatch: did traffic actually split the way it was configured?
 *
 * This is a validity gate rather than a metric. If assignment is broken (a variant
 * failing to apply, a redirect losing visitors, a bot filter hitting one arm harder)
 * then the two groups are not comparable and every p-value, interval and
 * recommendation on the page is measuring that fault rather than the change. It is
 * checked first because nothing below it means anything if it fails.
 *
 * The test is a chi-square goodness-of-fit against the configured weights. The
 * conventional alarm threshold is p < 0.0005 rather than 0.05: with real traffic the
 * split is never exactly nominal, and at 0.05 a healthy experiment trips the alarm
 * one time in twenty, which trains people to ignore it.
 *
 * Shared rather than colocated with the statistics screen: the cron detector that
 * raises an SRM notification has to reach the same verdict as the page a reader
 * lands on. Two copies of this maths would eventually disagree, and an alert that
 * contradicts the screen it links to is worse than no alert.
 */
import { normalCDF } from './normal-distribution';

export type VariantAllocation = {
  id: number;
  name: string;
  /** Configured share, as stored on the variant. Any positive scale works. */
  weight: number;
  sessions: number;
};

export type SampleRatioResult =
  | { state: 'ok'; pValue: number; rows: AllocationRow[] }
  | { state: 'mismatch'; pValue: number; rows: AllocationRow[] }
  | { state: 'not-applicable'; reason: string };

export type AllocationRow = {
  id: number;
  name: string;
  expectedShare: number;
  actualShare: number;
  expectedSessions: number;
  sessions: number;
  /** Signed relative deviation from expectation, for display. */
  deviation: number;
};

/** Below this, natural variation dominates and the test has no power worth reporting. */
const MIN_TOTAL_SESSIONS = 100;

/** Conventional SRM alarm level. Deliberately far stricter than 0.05: see above. */
export const SRM_ALARM = 0.0005;

export const checkSampleRatio = (variants: readonly VariantAllocation[]): SampleRatioResult => {
  const eligible = variants.filter((v) => v.weight > 0);

  if (eligible.length < 2) {
    return { state: 'not-applicable', reason: 'Needs at least two variants receiving traffic.' };
  }

  const totalSessions = eligible.reduce((sum, v) => sum + v.sessions, 0);
  if (totalSessions < MIN_TOTAL_SESSIONS) {
    return {
      state: 'not-applicable',
      reason: `Only ${totalSessions.toLocaleString()} sessions so far: too few for the split to mean anything.`,
    };
  }

  const totalWeight = eligible.reduce((sum, v) => sum + v.weight, 0);
  if (totalWeight <= 0) return { state: 'not-applicable', reason: 'No traffic weights are configured.' };

  const rows: AllocationRow[] = eligible.map((v) => {
    const expectedShare = v.weight / totalWeight;
    const expectedSessions = expectedShare * totalSessions;
    return {
      id: v.id,
      name: v.name,
      expectedShare,
      actualShare: v.sessions / totalSessions,
      expectedSessions,
      sessions: v.sessions,
      deviation: expectedSessions > 0 ? (v.sessions - expectedSessions) / expectedSessions : 0,
    };
  });

  // Pearson chi-square: sum (observed - expected)^2 / expected, with k-1 degrees of
  // freedom because the total is fixed by construction.
  const chiSquare = rows.reduce(
    (sum, r) => sum + (r.expectedSessions > 0 ? Math.pow(r.sessions - r.expectedSessions, 2) / r.expectedSessions : 0),
    0,
  );

  const pValue = chiSquareUpperTail(chiSquare, rows.length - 1);

  return { state: pValue < SRM_ALARM ? 'mismatch' : 'ok', pValue, rows };
};

/**
 * P(X² > x) for k degrees of freedom.
 *
 * Closed forms exist for the cases this needs. Even k reduces to a finite sum; odd k
 * needs the normal tail, which is why normalCDF is imported rather than a chi-square
 * table being embedded.
 */
export const chiSquareUpperTail = (x: number, k: number): number => {
  if (x <= 0) return 1;
  if (k <= 0) return 1;

  const even = k % 2 === 0;

  // Start from the exact value at the base degree, 1 or 2, then step up in twos.
  // Successive terms satisfy t(d+2) = t(d) · x/d, so no gamma function is needed and
  // the result stays exact for integer degrees of freedom.
  //
  // Q(x,2) = e^(-x/2) and Q(x,1) = 2(1 - Φ(√x)); the base term for the odd chain is
  // e^(-x/2)·√(2/πx), which stepped once gives the known
  // Q(x,3) = Q(x,1) + √(2x/π)·e^(-x/2).
  let q = even ? Math.exp(-x / 2) : 2 * (1 - normalCDF(Math.sqrt(x)));
  let term = even ? Math.exp(-x / 2) : Math.exp(-x / 2) * Math.sqrt(2 / (Math.PI * x));
  let degree = even ? 2 : 1;

  while (degree + 2 <= k) {
    term *= x / degree;
    q += term;
    degree += 2;
  }

  return Math.min(1, Math.max(0, q));
};
